/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  useCallback,
  useRef,
  useLayoutEffect,
  useState,
} from '@web-stories-wp/react';
import {
  Text,
  HexInput,
  NumericInput,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { focusStyle } from '../panels/shared';

const Preview = styled.button`
  margin: 0;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.defaultNormal};
  border-radius: 4px;
  background: transparent;
  text-align: left;
  color: ${({ theme }) => theme.colors.fg.primary};
  width: 100%;

  ${focusStyle};
`;

const Wrapper = styled.div`
  input {
    font-family: ${({ theme }) => theme.typography.family.primary};
    text-transform: lowercase;
    line-height: 18px;
  }
`;

const inputContainerStyle = css`
  padding: 8px;
`;

function EditablePreview({ label, value, format, onChange, hexColor }) {
  const isHex = Boolean(hexColor);
  const [isEditing, setIsEditing] = useState(false);
  const enableEditing = useCallback(() => setIsEditing(true), []);
  const disableEditing = useCallback(() => setIsEditing(false), []);
  const wrapperRef = useRef(null);

  // Both design-system inputs own draft validation and Escape cancellation.

  const handleOnBlur = (evt) => {
    // Ignore reason: There's no practical way to simulate the else occuring
    // istanbul ignore else
    if (!evt.currentTarget.contains(evt.relatedTarget)) {
      disableEditing();
    }
  };

  useLayoutEffect(() => {
    // Wait one tick to ensure the input has been loaded.
    const timeout = setTimeout(() => {
      const input = wrapperRef.current?.querySelector('input');
      if (isEditing && input) {
        input.focus();
        input.select();
      }
    });

    return () => clearTimeout(timeout);
  }, [isEditing, label, isHex]);

  if (!isEditing) {
    return (
      <Preview
        aria-label={label}
        onClick={enableEditing}
        onFocus={enableEditing}
      >
        <Text size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
          {format ? format(value) : value}
        </Text>
      </Preview>
    );
  }

  return (
    <Wrapper ref={wrapperRef} tabIndex={-1} onBlur={handleOnBlur}>
      {isHex ? (
        <HexInput
          aria-label={label}
          value={hexColor}
          onChange={onChange}
          containerStyleOverride={inputContainerStyle}
        />
      ) : (
        <NumericInput
          aria-label={label}
          value={Number(value)}
          min={0}
          max={100}
          isFloat={false}
          allowEmpty={false}
          canBeNegative={false}
          onChange={(_, percentage) => onChange(percentage)}
          containerStyleOverride={inputContainerStyle}
        />
      )}
    </Wrapper>
  );
}

EditablePreview.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  format: PropTypes.func,
  hexColor: PropTypes.object,
};

EditablePreview.defaultProps = {
  label: '',
  value: '',
};

export default EditablePreview;
